import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const PLATFORMS = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Messenger' },
  { key: 'shopify', label: 'Shopify' },
];

const MasterAdmin = () => {
  const [workspaces, setWorkspaces] = useState([]);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [shopifyByWs, setShopifyByWs] = useState({});      // ws._id -> { limit, callbackUrl, stores: [] }
  const [shopForm, setShopForm] = useState({});            // `${ws._id}:${index}` -> { clientId, clientSecret, shopDomain }
  const [limitDraft, setLimitDraft] = useState({});        // ws._id -> { whatsapp, instagram, facebook, shopify, name }
  const [savingWs, setSavingWs] = useState('');

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('sys_token') || ''}` } });

  const fetchWorkspaces = async () => {
    const res = await axios.get(`${API_URL}/api/workspaces`, auth());
    setWorkspaces(res.data);
    const entries = await Promise.all(res.data.map(ws =>
      axios.get(`${API_URL}/api/workspaces/${ws._id}/shopify`, auth()).then(r => [ws._id, r.data]).catch(() => [ws._id, null])
    ));
    setShopifyByWs(Object.fromEntries(entries));
  };

  useEffect(() => { fetchWorkspaces(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true); setMessage('');
    try {
      const res = await axios.post(`${API_URL}/api/workspaces`, { name }, auth());
      setMessage(`Workspace created — email: ${res.data.adminCredentials.email} · pass: ${res.data.adminCredentials.password}`);
      setName('');
      fetchWorkspaces();
    } catch { setMessage('Error creating workspace'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workspace and all linked user access? This cannot be undone.')) return;
    await axios.delete(`${API_URL}/api/workspaces/${id}`, auth());
    fetchWorkspaces();
  };

  const draftFor = (ws) => limitDraft[ws._id] || {
    name: ws.name,
    whatsapp: ws.integrationLimits?.whatsapp ?? 1,
    instagram: ws.integrationLimits?.instagram ?? 1,
    facebook: ws.integrationLimits?.facebook ?? 1,
    shopify: ws.integrationLimits?.shopify ?? 1,
  };
  const setDraft = (ws, patch) => setLimitDraft(prev => ({ ...prev, [ws._id]: { ...draftFor(ws), ...patch } }));

  const saveWorkspace = async (ws) => {
    const d = draftFor(ws);
    setSavingWs(ws._id);
    try {
      await axios.patch(`${API_URL}/api/workspaces/${ws._id}`, {
        name: d.name,
        integrationLimits: { whatsapp: d.whatsapp, instagram: d.instagram, facebook: d.facebook, shopify: d.shopify },
      }, auth());
      setLimitDraft(prev => { const n = { ...prev }; delete n[ws._id]; return n; });
      fetchWorkspaces();
    } catch (err) { alert(err.response?.data?.message || 'Save failed'); }
    finally { setSavingWs(''); }
  };

  const saveShopify = async (ws, index) => {
    const key = `${ws._id}:${index}`;
    const f = shopForm[key] || {};
    if (!f.clientId || !f.clientSecret || !f.shopDomain) { alert('Client ID, Client Secret and Shop Domain are all required.'); return; }
    try {
      await axios.post(`${API_URL}/api/workspaces/${ws._id}/shopify-config`, {
        connectionIndex: index,
        clientId: f.clientId.trim(), clientSecret: f.clientSecret.trim(), shopDomain: f.shopDomain.trim().toLowerCase(),
      }, auth());
      setShopForm(prev => ({ ...prev, [key]: { ...f, clientSecret: '' } }));
      fetchWorkspaces();
    } catch (err) { alert(err.response?.data?.message || 'Failed to save Shopify config'); }
  };

  const copy = (t) => navigator.clipboard.writeText(t);

  return (
    <div className="ma-wrap">
      <style>{css}</style>
      <header className="ma-head">
        <div>
          <div className="ma-logo"><span className="ma-logo-dot" /> Platform Control</div>
          <p className="ma-sub">Multi-tenant infrastructure · {workspaces.length} workspace{workspaces.length === 1 ? '' : 's'}</p>
        </div>
        <button className="ma-signout" onClick={() => { localStorage.removeItem('sys_token'); window.location.href = '/add-workspaces'; }}>Sign out</button>
      </header>

      <section className="ma-card ma-create">
        <div>
          <h2 className="ma-card-title">Deploy a new workspace</h2>
          <p className="ma-card-sub">Provisions a tenant + its first admin user instantly.</p>
        </div>
        <form onSubmit={handleCreate} className="ma-create-form">
          <input className="ma-input" placeholder="Organization name" value={name} onChange={e => setName(e.target.value)} required />
          <button className="ma-btn" type="submit" disabled={loading}>{loading ? 'Creating…' : 'Create workspace'}</button>
        </form>
        {message && <div className="ma-banner">{message}</div>}
      </section>

      <div className="ma-grid">
        {workspaces.map(ws => {
          const sh = shopifyByWs[ws._id];
          const d = draftFor(ws);
          const dirty = !!limitDraft[ws._id];
          const shopLimit = d.shopify;
          return (
            <div key={ws._id} className="ma-ws">
              <div className="ma-ws-top">
                <div className="ma-ws-name">{ws.name}</div>
                <span className="ma-pill-run"><span className="ma-dot" />Running</span>
              </div>

              <div className="ma-kv">
                <div><span className="ma-k">Workspace UUID</span><span className="ma-v ma-mono">{ws.workspaceUuid}</span></div>
                <div><span className="ma-k">Admin email</span><span className="ma-v ma-mono">{ws.generatedAdminEmail}</span></div>
                <div><span className="ma-k">Admin password</span><span className="ma-v ma-mono ma-pass">{ws.generatedAdminPassword}</span></div>
              </div>

              {/* Connection limits + rename */}
              <div className="ma-section">
                <div className="ma-section-h">Workspace settings</div>
                <label className="ma-field">
                  <span>Name</span>
                  <input className="ma-input-sm" value={d.name} onChange={e => setDraft(ws, { name: e.target.value })} />
                </label>
                <div className="ma-limits">
                  {PLATFORMS.map(p => (
                    <label key={p.key} className="ma-limit">
                      <span>{p.label}</span>
                      <input type="number" min="0" max="20" className="ma-num" value={d[p.key]}
                        onChange={e => setDraft(ws, { [p.key]: parseInt(e.target.value || '0', 10) })} />
                    </label>
                  ))}
                </div>
                <div className="ma-limits-hint">Max connections this workspace can hold per channel.</div>
                <button className={`ma-btn-sm ${dirty ? '' : 'ghost'}`} disabled={!dirty || savingWs === ws._id} onClick={() => saveWorkspace(ws)}>
                  {savingWs === ws._id ? 'Saving…' : dirty ? 'Save settings' : 'Saved'}
                </button>
              </div>

              {/* Per-store Shopify config */}
              <div className="ma-section">
                <div className="ma-section-h">Shopify stores <span className="ma-count">{(sh?.stores || []).filter(s => s.configured).length}/{shopLimit}</span></div>
                {sh?.callbackUrl && (
                  <div className="ma-callback">
                    <span className="ma-k">OAuth callback</span>
                    <div className="ma-callback-row">
                      <input readOnly value={sh.callbackUrl} className="ma-input-sm ma-mono" />
                      <button className="ma-copy" onClick={() => copy(sh.callbackUrl)}>Copy</button>
                    </div>
                  </div>
                )}
                {Array.from({ length: Math.max(1, shopLimit) }).map((_, index) => {
                  const store = (sh?.stores || []).find(s => s.index === index);
                  const key = `${ws._id}:${index}`;
                  const f = shopForm[key] || {};
                  const set = (patch) => setShopForm(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
                  const status = !store ? { t: 'Empty', c: '#6B6862', b: '#FAF7F1' }
                    : store.connected ? { t: 'Installed', c: '#111111', b: '#FAF7F1' }
                      : store.configured ? { t: 'Configured', c: '#d97706', b: '#fffbeb' }
                        : { t: 'Empty', c: '#6B6862', b: '#FAF7F1' };
                  return (
                    <div key={index} className="ma-store">
                      <div className="ma-store-h">
                        <span className="ma-store-n">Store {index + 1}{store?.shopDomain ? ` · ${store.shopDomain}` : ''}</span>
                        <span className="ma-store-pill" style={{ color: status.c, background: status.b }}>{status.t}</span>
                      </div>
                      <div className="ma-store-grid">
                        <input className="ma-input-sm" placeholder="Client ID" value={f.clientId ?? store?.clientId ?? ''} onChange={e => set({ clientId: e.target.value })} />
                        <input className="ma-input-sm" placeholder="store.myshopify.com" value={f.shopDomain ?? store?.shopDomain ?? ''} onChange={e => set({ shopDomain: e.target.value })} />
                        <input className="ma-input-sm ma-span2" type="password" placeholder="Client Secret (hidden after save)" value={f.clientSecret || ''} onChange={e => set({ clientSecret: e.target.value })} />
                        <button className="ma-btn-sm ma-span2" onClick={() => saveShopify(ws, index)}>Save store {index + 1}</button>
                      </div>
                      {store?.webhookRegistrationError && <div className="ma-err">Webhook error: {store.webhookRegistrationError}</div>}
                    </div>
                  );
                })}
              </div>

              <div className="ma-ws-foot">
                <span className="ma-deployed">Deployed {new Date(ws.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                <button className="ma-del" onClick={() => handleDelete(ws._id)}>Delete workspace</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const css = `
.ma-wrap{ min-height:100vh; background:#F9F4EE; padding:34px 44px 64px; color:#111111; font-family:'Space Grotesk',system-ui,sans-serif; }
.ma-head{ display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:26px; }
.ma-logo{ display:flex; align-items:center; gap:10px; font-size:24px; font-weight:800; letter-spacing:-0.03em; }
.ma-logo-dot{ width:11px; height:11px; border-radius:50%; background:#111111; box-shadow:0 0 0 4px #E7E1D6; }
.ma-sub{ color:#6B6862; font-size:13.5px; margin:5px 0 0; }
.ma-signout{ background:#fff; border:1px solid #E7E1D6; color:#111111; padding:9px 16px; border-radius:999px; font-weight:600; font-size:13px; cursor:pointer; }
.ma-signout:hover{ background:#111111; color:#fff; border-color:#111111; }

.ma-card{ background:#fff; border:1px solid #E7E1D6; border-radius:16px; padding:22px 24px; }
.ma-create{ margin-bottom:26px; display:flex; align-items:center; justify-content:space-between; gap:24px; flex-wrap:wrap; }
.ma-card-title{ font-size:17px; font-weight:800; margin:0; letter-spacing:-0.02em; }
.ma-card-sub{ font-size:13px; color:#9C978C; margin:4px 0 0; }
.ma-create-form{ display:flex; gap:10px; flex:1; min-width:300px; justify-content:flex-end; }
.ma-input{ flex:1; max-width:360px; padding:11px 14px; border:1px solid #E7E1D6; border-radius:10px; font-size:14px; outline:none; }
.ma-input:focus{ border-color:#111111; }
.ma-btn{ background:#111111; color:#fff; border:none; padding:11px 20px; border-radius:999px; box-shadow:4px 4px 0 rgba(17,17,17,.2); font-weight:700; font-size:14px; cursor:pointer; white-space:nowrap; }
.ma-btn:hover{ background:#000000; }
.ma-btn:disabled{ opacity:.6; }
.ma-banner{ flex-basis:100%; margin-top:16px; background:#FAF7F1; border:1px solid #E7E1D6; color:#111111; padding:13px 16px; border-radius:11px; font-size:13px; font-weight:600; }

.ma-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(420px,1fr)); gap:22px; }
.ma-ws{ background:#fff; border:1px solid #E7E1D6; border-radius:18px; padding:22px 22px 16px; display:flex; flex-direction:column; }
.ma-ws-top{ display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
.ma-ws-name{ font-size:18px; font-weight:800; letter-spacing:-0.02em; }
.ma-pill-run{ display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:#111111; background:#FAF7F1; padding:4px 11px; border-radius:20px; }
.ma-dot{ width:6px; height:6px; border-radius:50%; background:#111111; }

.ma-kv{ display:flex; flex-direction:column; gap:9px; background:#FAF7F1; border:1px solid #E7E1D6; border-radius:12px; padding:13px 15px; margin-bottom:16px; }
.ma-kv > div{ display:flex; flex-direction:column; gap:2px; }
.ma-k{ font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#9C978C; }
.ma-v{ font-size:13px; font-weight:700; color:#111111; word-break:break-all; }
.ma-mono{ font-family:ui-monospace,Menlo,monospace; font-weight:600; }
.ma-pass{ color:#B23A3A; }

.ma-section{ border-top:1px solid #FAF7F1; padding-top:15px; margin-top:4px; margin-bottom:6px; }
.ma-section-h{ font-size:13px; font-weight:800; color:#111111; margin-bottom:11px; display:flex; align-items:center; gap:8px; }
.ma-count{ font-size:11px; font-weight:700; color:#111111; background:#FAF7F1; padding:1px 8px; border-radius:20px; }
.ma-field{ display:flex; flex-direction:column; gap:5px; margin-bottom:11px; }
.ma-field > span{ font-size:11px; font-weight:700; color:#6B6862; }
.ma-input-sm{ padding:8px 11px; border:1px solid #E7E1D6; border-radius:8px; font-size:13px; outline:none; width:100%; box-sizing:border-box; }
.ma-input-sm:focus{ border-color:#111111; }
.ma-limits{ display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
.ma-limit{ display:flex; flex-direction:column; gap:5px; }
.ma-limit > span{ font-size:11px; font-weight:700; color:#3A3A37; }
.ma-num{ padding:8px; border:1px solid #E7E1D6; border-radius:8px; font-size:14px; font-weight:700; text-align:center; outline:none; }
.ma-num:focus{ border-color:#111111; }
.ma-limits-hint{ font-size:11px; color:#9C978C; margin:7px 0 11px; }
.ma-btn-sm{ background:#111111; color:#fff; border:none; padding:8px 14px; border-radius:999px; font-weight:700; font-size:12.5px; cursor:pointer; }
.ma-btn-sm:hover{ background:#000000; }
.ma-btn-sm.ghost{ background:#FAF7F1; color:#9C978C; cursor:default; }
.ma-btn-sm:disabled{ opacity:.7; }

.ma-callback{ margin-bottom:11px; }
.ma-callback-row{ display:flex; gap:7px; margin-top:4px; }
.ma-copy{ padding:8px 12px; background:#111111; color:#fff; border:none; border-radius:8px; font-weight:700; font-size:12px; cursor:pointer; }
.ma-store{ background:#FAF7F1; border:1px solid #E7E1D6; border-radius:11px; padding:12px 13px; margin-bottom:9px; }
.ma-store-h{ display:flex; align-items:center; justify-content:space-between; margin-bottom:9px; }
.ma-store-n{ font-size:12.5px; font-weight:700; color:#334155; }
.ma-store-pill{ font-size:10.5px; font-weight:800; padding:2px 9px; border-radius:20px; }
.ma-store-grid{ display:grid; grid-template-columns:1fr 1fr; gap:7px; }
.ma-span2{ grid-column:1 / 3; }
.ma-err{ margin-top:7px; font-size:11.5px; color:#c0392b; background:#fdecea; border-radius:7px; padding:6px 9px; }

.ma-ws-foot{ display:flex; align-items:center; justify-content:space-between; border-top:1px solid #FAF7F1; padding-top:13px; margin-top:10px; }
.ma-deployed{ font-size:12px; color:#9C978C; }
.ma-del{ background:none; border:none; color:#B23A3A; font-size:12.5px; font-weight:700; cursor:pointer; }
.ma-del:hover{ text-decoration:underline; }

@media (max-width:680px){ .ma-wrap{ padding:22px 16px 48px; } .ma-grid{ grid-template-columns:1fr; } .ma-limits{ grid-template-columns:repeat(2,1fr); } }
`;

export default MasterAdmin;
